// components/ventas/BusquedaUsuario.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Button, Alert, Input, Form, Spin, Tag } from 'antd';
import {
  UserOutlined, TeamOutlined, SearchOutlined, CheckCircleOutlined,
  IdcardOutlined, ArrowRightOutlined, ArrowLeftOutlined,
  LoadingOutlined, ReloadOutlined, CheckOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import VentasPresenciales from '../../../../service/VentaPresencial';
import TarjetaVecino from '../../../../service/tarjetaVecino';
import { useNotify } from '../../../../providers/NotificationProvider';

// Botón de "cliente de prueba": SOLO en local/staging (nunca en producción),
// gated por REACT_APP_ENV. El RUT es configurable por REACT_APP_QUICK_CLIENT_RUT.
const MOSTRAR_CLIENTE_PRUEBA = ['local', 'staging'].includes(process.env.REACT_APP_ENV);
const RUT_CLIENTE_PRUEBA = process.env.REACT_APP_QUICK_CLIENT_RUT || '11111111-1';

// Acepta "12.345.678-9", "12345678-9" o "123456789": se busca siempre sin puntos y con guion.
const normalizarRut = (valor = '') => {
  const limpio = String(valor).replace(/[.\s-]/g, '').toUpperCase();
  if (limpio.length < 2) return limpio;
  return `${limpio.slice(0, -1)}-${limpio.slice(-1)}`;
};
const RUT_VALIDO = /^\d{7,8}-[\dK]$/;

const getCategoriaEdad = (edad) => {
  if (edad === null || edad === undefined) return { texto: 'Sin edad', color: 'slate' };
  if (edad < 18) return { texto: 'Menor', color: 'blue' };
  if (edad >= 60) return { texto: 'Adulto Mayor', color: 'purple' };
  return { texto: 'Adulto', color: 'blue' };
};

export default function BusquedaUsuario({ ventaData, updateVentaData, nextStep, prevStep }) {
  const notify = useNotify();
  const [form] = Form.useForm();
  const buscandoRef = useRef(false); // evita doble búsqueda (Enter + click)
  const [noEncontrado, setNoEncontrado] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [validandoVecino, setValidandoVecino] = useState(false);
  const [usuarioEncontrado, setUsuarioEncontrado] = useState(null);
  const [esVecino, setEsVecino] = useState(false);
  const [estadoVecino, setEstadoVecino] = useState(null);
  const [tipoInscripcion, setTipoInscripcion] = useState('para_mi');
  const [selectedFamiliar, setSelectedFamiliar] = useState(null);
  // Estado vecino por familiar (validado en vivo por su propio RUT):
  // { [familiarId]: { loading, esVecino, mensaje, error } }
  const [familiaresVecino, setFamiliaresVecino] = useState({});

  // El descuento vecino se decide por el BENEFICIARIO seleccionado. Mantenemos
  // ventaData.esVecino sincronizado con quien esté elegido (titular o familiar)
  // para que la vista previa del precio refleje al participante correcto.
  // (El backend revalida y manda al pulsar "Continuar".)
  useEffect(() => {
    if (!usuarioEncontrado) return;
    if (tipoInscripcion === 'para_mi') {
      updateVentaData({ esVecino });
    } else if (selectedFamiliar) {
      const fv = familiaresVecino[selectedFamiliar];
      updateVentaData({ esVecino: fv && !fv.loading ? fv.esVecino === true : false });
    } else {
      updateVentaData({ esVecino: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoInscripcion, selectedFamiliar, esVecino, familiaresVecino, usuarioEncontrado]);

  // Valida en vivo la Tarjeta Vecino del familiar por SU propio RUT.
  const seleccionarFamiliar = async (familiar) => {
    setSelectedFamiliar(familiar._id);
    const cache = familiaresVecino[familiar._id];
    if (cache && !cache.loading && !cache.error) return; // ya validado
    setFamiliaresVecino(prev => ({ ...prev, [familiar._id]: { loading: true } }));
    try {
      // El servicio ya devuelve el `data` interno ({ esVecino, mensaje }). Antes se
      // leía `res.data` otra vez → undefined → todo familiar salía "sin Tarjeta Vecino".
      const res = await TarjetaVecino.Validar(familiar.rut);
      const data = res?.esVecino !== undefined ? res : (res?.data || {});
      setFamiliaresVecino(prev => ({
        ...prev,
        [familiar._id]: { loading: false, esVecino: data.esVecino === true, mensaje: data.mensaje }
      }));
    } catch (error) {
      setFamiliaresVecino(prev => ({
        ...prev,
        [familiar._id]: { loading: false, esVecino: false, error: true, mensaje: 'No se pudo validar Tarjeta Vecino' }
      }));
    }
  };

  // Se valida contra TODOS los talleres de la venta (antes solo el primero).
  const talleresVenta = ventaData.items?.length > 0
    ? ventaData.items.map(i => i.taller).filter(Boolean)
    : [ventaData.taller].filter(Boolean);
  const hayLimiteEdad = talleresVenta.some(t => t.edadMin || t.edadMax);

  const validarEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) {
      // Sin fecha solo se bloquea si algún taller tiene límite de edad (el backend
      // no puede verificarlo). Antes se bloqueaba siempre.
      return hayLimiteEdad
        ? { valido: false, mensaje: 'El cliente no tiene fecha de nacimiento registrada y el taller tiene límite de edad. Actualízala en su ficha.' }
        : { valido: true, edad: null };
    }
    const edad = dayjs().diff(dayjs(fechaNacimiento), 'year');
    for (const t of talleresVenta) {
      const pre = talleresVenta.length > 1 ? `${t.nombre}: ` : '';
      if (t.edadMin && edad < t.edadMin)
        return { valido: false, mensaje: `${pre}edad mínima ${t.edadMin} años (tiene ${edad}).` };
      if (t.edadMax && edad > t.edadMax)
        return { valido: false, mensaje: `${pre}edad máxima ${t.edadMax} años (tiene ${edad}).` };
    }
    return { valido: true, edad };
  };

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return null;
    return dayjs().diff(dayjs(fechaNacimiento), 'year');
  };

  const puedeConfirmar = () => {
    if (!usuarioEncontrado) return false;
    if (tipoInscripcion === 'para_mi') return validarEdad(usuarioEncontrado.birthdate).valido;
    if (!ventaData.cargas?.length) return false;
    return !!selectedFamiliar;
  };

  const buscarUsuario = async (values) => {
    if (buscandoRef.current) return;
    buscandoRef.current = true;
    const rut = normalizarRut(values.rut);
    try {
      setBuscando(true);
      setNoEncontrado(null);
      setValidandoVecino(true);
      const ventaIds = ventaData.items?.length > 0
        ? ventaData.items.map(i => i.ventaId)
        : [ventaData.ventaId];

      let lastResponse = null;
      for (const vid of ventaIds) {
        const response = await VentasPresenciales.BuscarORegistrarUsuario(vid, { rut });
        if (response.success) lastResponse = response;
        else {
          // El backend responde 200 con success:false cuando el RUT no tiene cuenta.
          // Antes la pantalla no decía nada.
          setNoEncontrado({ rut, mensaje: response.mensaje || response.message });
          return;
        }
      }
      if (lastResponse?.success) {
        setUsuarioEncontrado(lastResponse.data.usuario);
        setEsVecino(lastResponse.data.esVecino);
        setEstadoVecino({ esVecino: lastResponse.data.esVecino, mensaje: lastResponse.data.estadoVecino });
        updateVentaData({
          usuario: lastResponse.data.usuario,
          esVecino: lastResponse.data.esVecino,
          cargas: lastResponse.data.cargas || []
        });
        notify.success('Usuario encontrado correctamente');
      }
    } catch (error) {
      notify.error(error.message || 'No se pudo buscar el usuario');
    } finally {
      setBuscando(false);
      setValidandoVecino(false);
      buscandoRef.current = false;
    }
  };

  const handleSeleccionarBeneficiario = async () => {
    if (!usuarioEncontrado) { notify.warning('Debes buscar un usuario primero'); return; }
    try {
      setBuscando(true);
      const payload = {
        userId: usuarioEncontrado.userId || usuarioEncontrado.sub || usuarioEncontrado._id,
        familiarId: tipoInscripcion === 'para_familiar' ? selectedFamiliar : null
      };
      const ventaIds = ventaData.items?.length > 0
        ? ventaData.items.map(i => i.ventaId)
        : [ventaData.ventaId];
      let inscrito = null;
      let esVecinoBackend = false;
      for (const vid of ventaIds) {
        const response = await VentasPresenciales.SeleccionarBeneficiario(vid, payload);
        if (!response.success) throw new Error('No se pudo seleccionar el beneficiario');
        inscrito = response.data.inscrito;
        // El backend revalida el RUT del beneficiario y manda sobre la vista previa.
        esVecinoBackend = response.data.esVecino === true;
      }
      updateVentaData({ inscrito, tipoInscripcion, esVecino: esVecinoBackend });
      notify.success('Beneficiario seleccionado para todos los talleres');
      nextStep();
    } catch (error) {
      notify.error(error.message || 'No se pudo seleccionar el beneficiario');
    } finally {
      setBuscando(false);
    }
  };

  const handleBuscarOtroCliente = () => {
    setUsuarioEncontrado(null);
    setEsVecino(false);
    setEstadoVecino(null);
    setTipoInscripcion('para_mi');
    setSelectedFamiliar(null);
    setFamiliaresVecino({});
    setNoEncontrado(null);
    form.resetFields();
    updateVentaData({ usuario: null, esVecino: false, cargas: [], inscrito: null, tipoInscripcion: 'para_mi' });
  };

  // ── Render: sin usuario ────────────────────────────────────────────────────
  if (!usuarioEncontrado) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Header */}
          <div className="px-6 py-5 border-b border-slate-100">
            <h3 className="font-bold text-slate-800 text-lg">Identificar al <span className="hl-amarillo">cliente</span></h3>
            <p className="text-slate-500 text-sm mt-1">
              Ingresa el RUT para buscar o registrar al cliente en el sistema.
            </p>
          </div>

          {/* Form */}
          <div className="px-6 py-6">
            <Form form={form} onFinish={buscarUsuario} layout="vertical">
              <Form.Item
                name="rut"
                label={
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    RUT del cliente
                  </span>
                }
                rules={[
                  { required: true, message: 'Ingresa el RUT' },
                  {
                    validator: (_, v) => !v || RUT_VALIDO.test(normalizarRut(v))
                      ? Promise.resolve()
                      : Promise.reject(new Error('RUT inválido. Ej: 12.345.678-9'))
                  }
                ]}
              >
                <Input
                  size="large"
                  placeholder="12.345.678-9"
                  prefix={<IdcardOutlined className="text-slate-400" />}
                  suffix={buscando ? <LoadingOutlined /> : null}
                  className="font-mono text-base"
                />
              </Form.Item>

              {noEncontrado && (
                <Alert
                  type="warning" showIcon className="mb-3"
                  message={`No hay una cuenta con el RUT ${noEncontrado.rut}`}
                  description="El cliente debe crear su cuenta en la web antes de inscribirse. Revisa también que el RUT esté bien escrito."
                />
              )}

              <Button
                type="primary" size="large" htmlType="submit"
                loading={buscando} icon={<SearchOutlined />} block
                className="h-11 font-semibold mt-2"
              >
                Buscar cliente
              </Button>

              {/* 🧪 SOLO local/staging — autollenar y buscar un cliente de prueba */}
              {MOSTRAR_CLIENTE_PRUEBA && (
                <Button
                  type="dashed" size="large" block
                  disabled={buscando}
                  className="mt-2"
                  onClick={() => {
                    form.setFieldsValue({ rut: RUT_CLIENTE_PRUEBA });
                    buscarUsuario({ rut: RUT_CLIENTE_PRUEBA });
                  }}
                >
                  🧪 Cliente de prueba ({RUT_CLIENTE_PRUEBA})
                </Button>
              )}
            </Form>
          </div>

          {/* Footer nav */}
          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50">
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={prevStep}
              type="text"
              className="text-slate-500"
            >
              Volver al paso anterior
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: usuario encontrado ─────────────────────────────────────────────
  const edad = calcularEdad(usuarioEncontrado.birthdate);
  const categoria = getCategoriaEdad(edad);
  const validacion = validarEdad(usuarioEncontrado.birthdate);

  const familiares = ventaData.cargas || [];

  return (
    <Spin spinning={buscando}>
      <div className="max-w-2xl mx-auto space-y-4">

        {/* ── Tarjeta del usuario ──────────────────────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center
            justify-between">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Cliente identificado
            </span>
            <button
              onClick={handleBuscarOtroCliente}
              className="flex items-center gap-1.5 text-xs text-blue-600
                hover:text-blue-700 font-medium transition-colors"
            >
              <ReloadOutlined style={{ fontSize: 11 }} />
              Buscar otro cliente
            </button>
          </div>

          <div className="p-5">
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div className="w-12 h-12 rounded-full bg-[#fffbea] flex items-center
                justify-center flex-shrink-0 border-2 border-[#fde68a]">
                <UserOutlined className="text-[#5c4300] text-xl" />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-slate-800 text-lg">
                    {usuarioEncontrado.nombre}
                  </span>
                  {esVecino && (
                    <span className="inline-flex items-center gap-1 bg-[#feda2c]
                      text-[#5c4300] text-xs font-semibold px-2 py-0.5 rounded-full">
                      <IdcardOutlined style={{ fontSize: 10 }} />
                      Titular con Tarjeta Vecino
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 mt-3">
                  {[
                    { label: 'RUT', value: usuarioEncontrado.rut },
                    { label: 'Email', value: usuarioEncontrado.email },
                    { label: 'Teléfono', value: usuarioEncontrado.telefono },
                    {
                      label: 'Edad',
                      value: edad !== null ? (
                        <span className="flex items-center gap-1.5">
                          {edad} años
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${categoria.color === 'purple'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-blue-100 text-blue-700'
                            }`}>{categoria.texto}</span>
                        </span>
                      ) : '—'
                    },
                  ].map(({ label, value }) => value && (
                    <div key={label}>
                      <div className="text-xs text-slate-400 font-medium">{label}</div>
                      <div className="text-sm text-slate-700 font-medium">{value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Estado vecino */}
            {estadoVecino && (
              <div className={`mt-4 px-4 py-3 rounded-lg border text-sm ${estadoVecino.esVecino
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-slate-50 border-slate-200 text-slate-600'
                }`}>
                {estadoVecino.esVecino ? (
                  <span className="flex items-center gap-2">
                    <CheckCircleOutlined />
                    <span>El <b>titular</b> tiene Tarjeta Vecino vigente. El descuento aplica según el <b>beneficiario</b> de la inscripción.</span>
                  </span>
                ) : (
                  <span>{estadoVecino.mensaje || 'El titular no tiene Tarjeta Vecino activa.'}</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Selección de beneficiario ────────────────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              ¿Para quién es la inscripción?
            </span>
          </div>

          <div className="p-4 space-y-2">
            {/* Para el titular */}
            <div
              onClick={() => { setTipoInscripcion('para_mi'); setSelectedFamiliar(null); }}
              className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer
                transition-all ${tipoInscripcion === 'para_mi'
                  ? 'border-[#0047bb] bg-blue-50'
                  : 'border-slate-200 hover:border-slate-300'
                }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center
                flex-shrink-0 ${tipoInscripcion === 'para_mi' ? 'bg-[#0047bb]' : 'bg-slate-100'
                }`}>
                <UserOutlined className={
                  tipoInscripcion === 'para_mi' ? 'text-white' : 'text-slate-400'
                } />
              </div>
              <div className="flex-1">
                <div className={`font-semibold text-sm ${tipoInscripcion === 'para_mi' ? 'text-[#00266b]' : 'text-slate-700'
                  }`}>
                  Para el titular
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  La inscripción será para {usuarioEncontrado.nombre}
                </div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center
                justify-center flex-shrink-0 ${tipoInscripcion === 'para_mi'
                  ? 'border-[#0047bb] bg-[#0047bb]'
                  : 'border-slate-300'
                }`}>
                {tipoInscripcion === 'para_mi' && (
                  <CheckOutlined style={{ fontSize: 10, color: 'white' }} />
                )}
              </div>
            </div>

            {/* Para un familiar */}
            <div
              onClick={() => { setTipoInscripcion('para_familiar'); setSelectedFamiliar(null); }}
              className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer
                transition-all ${tipoInscripcion === 'para_familiar'
                  ? 'border-[#0047bb] bg-blue-50'
                  : 'border-slate-200 hover:border-slate-300'
                }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center
                flex-shrink-0 mt-0.5 ${tipoInscripcion === 'para_familiar' ? 'bg-[#0047bb]' : 'bg-slate-100'
                }`}>
                <TeamOutlined className={
                  tipoInscripcion === 'para_familiar' ? 'text-white' : 'text-slate-400'
                } />
              </div>
              <div className="flex-1">
                <div className={`font-semibold text-sm ${tipoInscripcion === 'para_familiar' ? 'text-[#00266b]' : 'text-slate-700'
                  }`}>
                  Para un familiar
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Selecciona un familiar de la lista del titular
                </div>

                {/* Lista de familiares */}
                {tipoInscripcion === 'para_familiar' && (
                  <div className="mt-3 space-y-1" onClick={e => e.stopPropagation()}>
                    {familiares.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">
                        Este usuario no tiene familiares registrados.
                      </p>
                    ) : familiares.map(f => {
                      const edadF = f.edad != null ? f.edad : calcularEdad(f.fechaNacimiento || f.birthdate);
                      const catF = getCategoriaEdad(edadF);
                      const isSel = selectedFamiliar === f._id;
                      const fv = familiaresVecino[f._id];
                      return (
                        <div
                          key={f._id}
                          onClick={() => seleccionarFamiliar(f)}
                          className={`flex items-center gap-3 p-3 rounded-lg border
                            cursor-pointer transition-all ${isSel
                              ? 'border-blue-400 bg-white'
                              : 'border-slate-200 bg-white hover:border-slate-300'
                            }`}
                        >
                          <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0
                            flex items-center justify-center ${isSel ? 'border-[#0047bb] bg-[#0047bb]' : 'border-slate-300'
                            }`}>
                            {isSel && <CheckOutlined style={{ fontSize: 8, color: 'white' }} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-slate-700">
                              {f.nombre}
                            </span>
                            <span className="text-xs text-slate-400 ml-2">
                              {edadF != null ? `${edadF} años · ${catF.texto}` : catF.texto}
                            </span>
                          </div>
                          {/* Estado Tarjeta Vecino del familiar (validado por su propio RUT) */}
                          {fv?.loading ? (
                            <Tag icon={<LoadingOutlined />} className="m-0">Validando…</Tag>
                          ) : fv?.error ? (
                            <Tag color="warning" className="m-0">Sin validar</Tag>
                          ) : fv?.esVecino ? (
                            <Tag icon={<IdcardOutlined />} color="blue" className="m-0">Tarjeta Vecino</Tag>
                          ) : fv ? (
                            <Tag color="default" className="m-0">Sin Tarjeta Vecino</Tag>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center
                justify-center flex-shrink-0 mt-0.5 ${tipoInscripcion === 'para_familiar'
                  ? 'border-[#0047bb] bg-[#0047bb]'
                  : 'border-slate-300'
                }`}>
                {tipoInscripcion === 'para_familiar' && (
                  <CheckOutlined style={{ fontSize: 10, color: 'white' }} />
                )}
              </div>
            </div>
          </div>

          {/* Validación de edad para el titular */}
          {tipoInscripcion === 'para_mi' && !validacion.valido && (
            <div className="mx-4 mb-4">
              <Alert
                message={`Edad fuera del rango: ${validacion.mensaje}`}
                type="error" showIcon
              />
            </div>
          )}
        </div>

        {/* ── Navegación ───────────────────────────────────────────────── */}
        <div className="flex items-center justify-between">
          <Button icon={<ArrowLeftOutlined />} onClick={prevStep} size="large">
            Anterior
          </Button>
          <Button
            type="primary" size="large"
            icon={<ArrowRightOutlined />}
            onClick={handleSeleccionarBeneficiario}
            loading={buscando}
            disabled={!puedeConfirmar()}
            className="h-11 px-8 font-semibold"
          >
            Continuar
          </Button>
        </div>
      </div>
    </Spin>
  );
}
